/**
 * Search, one segmented choice and a few toggles above a list. Every value lives in the URL (`q`, `show`, `only`),
 * so a shared link opens the same view and Back undoes a filter. The section reads the same values with useFilters.
 */
import { useParam } from '../hooks/useParam'
import { cx } from '../cx'
import { Input } from '../primitives/Input'
import { Segmented, type SegmentOption } from '../primitives/Segmented'
import s from './FilterBar.module.css'

export interface FilterChip {
  value: string
  label: string
}

export interface Filters {
  q: string
  /** The segment picked; the first segment when the URL names none. */
  show: string
  only: string[]
  setQ: (q: string) => void
  setShow: (v: string) => void
  toggle: (v: string) => void
  clear: () => void
  active: boolean
}

export function useFilters(segments?: SegmentOption[]): Filters {
  const q = useParam('q')
  const show = useParam('show')
  const only = useParam('only')
  const first = segments?.[0]?.value ?? ''
  const onlyList = only.value ? only.value.split(',').filter(Boolean) : []
  return {
    q: q.value ?? '',
    show: show.value ?? first,
    only: onlyList,
    setQ: (v) => q.set(v, { replace: true }),
    setShow: (v) => (v === first ? show.clear() : show.set(v)),
    toggle: (v) => only.set(onlyList.includes(v) ? onlyList.filter((x) => x !== v).join(',') : [...onlyList, v].join(',')),
    clear: () => (q.clear(), show.clear(), only.clear()),
    active: Boolean(q.value || show.value || onlyList.length),
  }
}

export function FilterBar({ search, segments, chips, label = 'Filters' }: { /** The search field's placeholder; no field without it. */ search?: string; segments?: SegmentOption[]; chips?: FilterChip[]; label?: string }) {
  const f = useFilters(segments)
  return (
    <div className={s.box}>
      <div className={s.bar} role="search" aria-label={label}>
        {search && (
          <span className={s.search}>
            <Input search aria-label={search} placeholder={search} value={f.q} onChange={(e) => f.setQ(e.target.value)} />
          </span>
        )}
        {segments && <Segmented label="Show" size="sm" options={segments} value={f.show} onChange={f.setShow} />}
        {chips?.map((c) => {
          const on = f.only.includes(c.value)
          return (
            <button key={c.value} type="button" className={cx(s.chip, on && s.on)} aria-pressed={on} onClick={() => f.toggle(c.value)}>
              {c.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}
